import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Paperclip, ImageOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'

const MAX_ATTACHMENTS = 5
const MAX_SINGLE_FILE_BYTES = 20 * 1024 * 1024 // 20MB
const MAX_CHARS = 2000

interface BugReportModalSubmitData {
  name?: string
  email?: string
  message: string
  attachments: File[]
}

interface BugReportModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: BugReportModalSubmitData) => Promise<void>
  defaultName?: string
  defaultEmail?: string
  screenshotDataUrl?: string | null
  onRemoveScreenshot?: () => void
  /** Annotators: suppress the screenshot preview/lightbox (still attached to the report). */
  hideScreenshotPreview?: boolean
  screenshotHiddenLabel?: string
  title: string
  subtitle?: string
  labelName: string
  labelEmail: string
  labelComments: string
  placeholderComments?: string
  labelAttachments: string
  labelAttachMedia: string
  getTooManyAttachmentsError: (max: number) => string
  getFileTooLargeError: (fileName: string, maxMb: number) => string
  labelClose: string
  labelSubmit: string
  labelSubmitting: string
  isLoading?: boolean
}

interface State {
  name: string
  email: string
  message: string
  attachments: File[]
  isSubmitting: boolean
}

type Action =
  | { type: 'SET_NAME'; value: string }
  | { type: 'SET_EMAIL'; value: string }
  | { type: 'SET_MESSAGE'; value: string }
  | { type: 'SET_ATTACHMENTS'; value: File[] }
  | { type: 'SET_SUBMITTING'; value: boolean }
  | { type: 'RESET'; name: string; email: string }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.value }
    case 'SET_EMAIL':
      return { ...state, email: action.value }
    case 'SET_MESSAGE':
      return { ...state, message: action.value }
    case 'SET_ATTACHMENTS':
      return { ...state, attachments: action.value }
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value }
    case 'RESET':
      return {
        name: action.name,
        email: action.email,
        message: '',
        attachments: [],
        isSubmitting: false,
      }
  }
}

const BugReportModalLegacy = ({
  isOpen,
  onClose,
  onSubmit,
  defaultName,
  defaultEmail,
  screenshotDataUrl,
  onRemoveScreenshot,
  hideScreenshotPreview = false,
  screenshotHiddenLabel = 'Screenshot attached to your report',
  title,
  subtitle,
  labelComments,
  placeholderComments,
  labelAttachments,
  labelAttachMedia,
  getTooManyAttachmentsError,
  getFileTooLargeError,
  labelClose,
  labelSubmit,
  labelSubmitting,
  isLoading = false,
}: BugReportModalProps) => {
  const [state, dispatch] = useReducer(reducer, {
    name: defaultName || '',
    email: defaultEmail || '',
    message: '',
    attachments: [],
    isSubmitting: false,
  })

  const anyLoading = isLoading || state.isSubmitting
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [screenshotPreviewOpen, setScreenshotPreviewOpen] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return
    setScreenshotPreviewOpen(false)
    dispatch({ type: 'RESET', name: defaultName || '', email: defaultEmail || '' })
  }, [isOpen, defaultName, defaultEmail])

  // Escape key handler — dismiss lightbox first, then modal
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (screenshotPreviewOpen) {
          setScreenshotPreviewOpen(false)
        } else {
          handleClose()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files)
      if (incoming.length === 0) return

      const next: File[] = [...state.attachments]

      for (const file of incoming) {
        if (next.length >= MAX_ATTACHMENTS) {
          toast.error(getTooManyAttachmentsError(MAX_ATTACHMENTS))
          break
        }
        if (file.size > MAX_SINGLE_FILE_BYTES) {
          toast.error(getFileTooLargeError(file.name, 20))
          continue
        }
        const dup = next.some(
          (f) =>
            f.name === file.name &&
            f.size === file.size &&
            f.lastModified === file.lastModified
        )
        if (!dup) next.push(file)
      }

      dispatch({ type: 'SET_ATTACHMENTS', value: next })
    },
    [state.attachments, getTooManyAttachmentsError, getFileTooLargeError]
  )

  const removeAttachmentAt = (idx: number) => {
    dispatch({
      type: 'SET_ATTACHMENTS',
      value: state.attachments.filter((_, i) => i !== idx),
    })
  }

  const handleClose = () => {
    if (anyLoading) return
    onClose()
  }

  const handleSubmit = async () => {
    if (anyLoading) return
    if (!state.message.trim()) return

    dispatch({ type: 'SET_SUBMITTING', value: true })
    try {
      // Convert screenshot data URL to File if present
      const allAttachments = [...state.attachments]
      if (screenshotDataUrl) {
        const res = await fetch(screenshotDataUrl)
        const blob = await res.blob()
        allAttachments.unshift(
          new File([blob], `screenshot-${Date.now()}.jpg`, { type: 'image/jpeg' })
        )
      }

      await onSubmit({
        name: state.name.trim() || undefined,
        email: state.email.trim() || undefined,
        message: state.message.trim(),
        attachments: allAttachments,
      })
      onClose()
    } catch (err) {
      console.error('Failed to submit bug report', err)
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false })
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div
      role="presentation"
      // TOP LAYER, matching BugReportModalV2 — see the comment there for why.
      // This is the copy PRODUCTION renders (`useUIv2()` is off there), so raising
      // only the V2 modal would have fixed the bug in dev and left it in prod.
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
        className="bg-white dark:bg-dark-card rounded-lg shadow-lg w-full max-w-xl mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between p-5 pb-3 border-b border-gray-200 dark:border-dark-border"
        >
          <div className="flex-1">
            <h3
              id="bug-report-title"
              className="text-lg font-semibold mb-1 text-gray-900 dark:text-dark-text"
            >
              {title}
            </h3>
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            disabled={anyLoading}
            className="ml-4 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-400 dark:text-gray-500"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Textarea with character counter */}
          <div>
            <label className="text-sm font-medium text-gray-800 dark:text-dark-text">
              {labelComments}
            </label>
            <Textarea
              className="w-full min-h-[120px] resize-none mt-1"
              placeholder={placeholderComments || labelComments}
              value={state.message}
              maxLength={MAX_CHARS}
              onChange={(e) =>
                dispatch({ type: 'SET_MESSAGE', value: e.target.value })
              }
              disabled={anyLoading}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleSubmit()
                }
              }}
            />
            <div
              className="text-xs text-right mt-1 text-gray-400 dark:text-gray-500"
            >
              {state.message.length}/{MAX_CHARS}
            </div>
          </div>

          {/* Name / Email — hidden, captured automatically from auth session */}
          {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-800">
                {labelName}
              </label>
              <input
                value={state.name}
                onChange={(e) =>
                  dispatch({ type: 'SET_NAME', value: e.target.value })
                }
                disabled={anyLoading}
                placeholder={labelName}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-800">
                {labelEmail}
              </label>
              <input
                value={state.email}
                onChange={(e) =>
                  dispatch({ type: 'SET_EMAIL', value: e.target.value })
                }
                disabled={anyLoading}
                placeholder={labelEmail}
                type="email"
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div> */}

          {/* Screenshot thumbnail */}
          {screenshotDataUrl && (hideScreenshotPreview ? (
            // Annotator: no reachable full-res <img> (no right-click-save / lightbox);
            // the screenshot is still attached to the submitted report.
            <div
              data-testid="bug-report-screenshot-hidden"
              className="flex h-20 items-center gap-2 rounded border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-muted px-3 text-sm text-gray-500 dark:text-dark-text-muted"
            >
              <ImageOff className="w-4 h-4 shrink-0" />
              <span>{screenshotHiddenLabel}</span>
            </div>
          ) : (
            <div className="relative inline-block">
              <img
                src={screenshotDataUrl}
                alt="Screenshot"
                role="button"
                tabIndex={0}
                className="h-20 rounded border object-cover cursor-pointer hover:opacity-80 transition-opacity border-gray-200 dark:border-dark-border"
                onClick={() => setScreenshotPreviewOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setScreenshotPreviewOpen(true)
                  }
                }}
              />
              <button
                type="button"
                className="absolute -top-1.5 -right-1.5 rounded-full bg-white dark:bg-dark-card border shadow-sm p-0.5 hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors border-gray-300 dark:border-gray-600 text-gray-500 dark:text-dark-text-muted"
                onClick={onRemoveScreenshot}
                disabled={anyLoading}
              >
                <X size={12} />
              </button>
            </div>
          ))}

          {/* Screenshot lightbox */}
          {screenshotPreviewOpen && screenshotDataUrl && !hideScreenshotPreview && createPortal(
            <div
              role="presentation"
              // Above the report form itself, which is now the top layer.
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10100] cursor-pointer"
              onClick={() => setScreenshotPreviewOpen(false)}
            >
              <img
                src={screenshotDataUrl}
                alt="Screenshot preview"
                className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>,
            document.body
          )}

          {/* Attachment list */}
          {state.attachments.length > 0 && (
            <div>
              <div
                className="text-sm font-medium mb-2 text-gray-800 dark:text-dark-text"
              >
                {labelAttachments}
              </div>
              <div className="space-y-1">
                {state.attachments.map((file, idx) => (
                  <div
                    key={`${file.name}-${file.size}-${idx}`}
                    className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted text-gray-700 dark:text-gray-300"
                  >
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachmentAt(idx)}
                      disabled={anyLoading}
                      className="shrink-0 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 text-gray-400 dark:text-gray-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between gap-3 p-5 pt-3 border-t border-gray-200 dark:border-dark-border"
        >
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={anyLoading || state.attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
              className="gap-1.5"
            >
              <Paperclip className="w-4 h-4" />
              {labelAttachMedia}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={anyLoading}
            >
              {labelClose}
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!state.message.trim() || anyLoading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {anyLoading ? labelSubmitting : labelSubmit}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default BugReportModalLegacy
